import { useEffect, useState } from "react";
import Joi from "joi-browser";
import { defaultVoiceAssistantNode } from "../../../Models/Assistant";

export default useVoiceCallActionForm;

const noAnswerRechurnSchema = {
  noAnswerRechurnEnabled: Joi.boolean().label("No Answer Rechurn"),
  noAnswerRechurnAttemptsPerDay: Joi.when("noAnswerRechurnEnabled", {
    is: true,
    then: Joi.number().min(1).required().label("Attempts Per Day"),
    otherwise: Joi.number().min(0).label("Attempts Per Day"),
  }).label("Attempts Per Day"),
  noAnswerRechurnTotalDays: Joi.when("noAnswerRechurnEnabled", {
    is: true,
    then: Joi.number().min(1).required().label("Total Retry Days"),
    otherwise: Joi.number().min(0).label("Total Retry Days"),
  }),
  noAnswerRechurnRetryIntervalMinutes: Joi.when("noAnswerRechurnEnabled", {
    is: true,
    then: Joi.number().min(1).required().label("Retry Interval"),
    otherwise: Joi.number().min(0).label("Retry Interval"),
  }),
  noAnswerRechurnStoppingCriteriaFieldName: Joi.string().allow(null, "").label("Stopping Criteria Field"),
  noAnswerRechurnStoppingCriteriaFieldValue: Joi.string().allow(null, "").label("Stopping Criteria Value")
};

export const voiceCallActionFormSchema = {
  name: Joi.string().required().label("Name"),
  description: Joi.string().allow(null, "").label("Description"),
  assistantId: Joi.string().required().disallow("").label("Assistant"),
  phoneNumberField: Joi.string()
    .required()
    .disallow("")
    .label("Phone Number Mapping"),
  fromIdentifier: Joi.string().disallow("").required().label("From Number"),
  ...noAnswerRechurnSchema,
};

const getVoiceCallActionNoAnswerRechurnConfigureData = (voiceCallAction) => {
  return {
    noAnswerRechurnEnabled:
      voiceCallAction?.noAnswerRechurn?.enabled ??
      defaultVoiceAssistantNode.noAnswerRechurn.enabled,
    noAnswerRechurnAttemptsPerDay:
      voiceCallAction?.noAnswerRechurn?.attemptsPerDay ??
      defaultVoiceAssistantNode.noAnswerRechurn.attemptsPerDay,
    noAnswerRechurnTotalDays:
      voiceCallAction?.noAnswerRechurn?.totalDays ??
      defaultVoiceAssistantNode.noAnswerRechurn.totalDays,
    noAnswerRechurnRetryIntervalMinutes:
      voiceCallAction?.noAnswerRechurn?.retryIntervalMinutes ??
      defaultVoiceAssistantNode.noAnswerRechurn.retryIntervalMinutes,
    noAnswerRechurnStoppingCriteriaFieldName:
      voiceCallAction?.noAnswerRechurn?.stoppingCriteria?.fieldName ??
      defaultVoiceAssistantNode.noAnswerRechurn.stoppingCriteria.fieldName,
    noAnswerRechurnStoppingCriteriaFieldValue:
      voiceCallAction?.noAnswerRechurn?.stoppingCriteria?.fieldValue ??
      defaultVoiceAssistantNode.noAnswerRechurn.stoppingCriteria.fieldValue,
  };
};

export const getVoiceCallActionConfigureData = (voiceCallAction) => {
  return {
    name: voiceCallAction.name,
    description: voiceCallAction.description,
    assistantId: voiceCallAction.assistantId,
    phoneNumberField: voiceCallAction.inputMapping.phoneNumberField,
    fromIdentifier: voiceCallAction.fromIdentifier,
    ...getVoiceCallActionNoAnswerRechurnConfigureData(voiceCallAction),
  };
};

function useVoiceCallActionForm(voiceCallAction, validationErrors = {}) {
  const [formState, setFormState] = useState(() =>
    getVoiceCallActionConfigureData(voiceCallAction),
  );

  const [errors, setErrors] = useState(validationErrors);

  useEffect(() => {
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    }
  }, [validationErrors]);

  const updateFormState = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const schema = Joi.object(voiceCallActionFormSchema);
    const { error } = schema.validate(formState, { abortEarly: false });

    if (!error) {
      setErrors({});
      return true;
    }

    const fieldErrors = {};
    error.details.forEach((detail) => {
      fieldErrors[detail.path[0]] = detail.message;
    });
    setErrors(fieldErrors);
    return false;
  };

  return {
    formState,
    updateFormState,
    validate,
    errors,
  };
}
